import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Lock,
  BookOpen,
  Leaf,
  BarChart3,
  Award,
  ChevronRight,
  Menu,
  X,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "@/config/auth";
import { BRAND } from "@/lib/branding";

export default function ModeSelection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const goBasic = () => {
    try { localStorage.setItem("upsi_mode", "basic"); } catch (e) { /* noop */ }
    navigate("/basic");
  };

  const goAdvanced = () => {
    try { localStorage.setItem("upsi_mode", "advanced"); } catch (e) { /* noop */ }
    if (user) navigate("/advanced");
    else navigate("/login", { state: { from: "/advanced" } });
  };

  const goDocuments = () => {
    if (user) navigate("/documents");
    else navigate("/login", { state: { from: "/documents" } });
  };

  useEffect(() => {
    document.title = `${BRAND.short} — ${BRAND.full}`;
  }, []);

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-white text-[#0A1A0F]"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      data-testid="landing-page"
    >
      {/* ───────── BOLD DIAGONAL SPLIT: White & Artwork ───────── */}
      {/* Desktop: diagonal split */}
      <div className="hidden md:block pointer-events-none absolute inset-0">
        {/* White half - top left */}
        <div
          className="absolute inset-0"
          style={{
            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
            background: 'white',
          }}
        />

        {/* Artwork half - bottom right */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
          }}
        >
          {/* Green fallback background */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 40%, #43A047 70%, #4CAF50 100%)',
            }}
          />

          {/* Artwork layer - positioned with 10px inset from the diagonal edge */}
          <div
            className="absolute inset-0"
            style={{
              clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
              transform: 'translate(0px, 10px) scale(1)',
              transformOrigin: 'top right',
              opacity: 0.08,
            }}
          >
            <img
              src="/red1.png"
              alt="Sustainability artwork"
              className="w-full h-full object-cover object-center"
              style={{
                minWidth: '100%',
                minHeight: '100%',
                clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                filter: 'brightness(0) saturate(100%) invert(1)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Mobile: White top section with diagonal transition to green */}
      <div className="md:hidden fixed inset-0 pointer-events-none z-0">
        {/* White section - top */}
        <div
          className="absolute inset-0"
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% 20%, 0 45%)',
            background: '#FFFFFF',
          }}
        />

        {/* Green section with artwork - bottom */}
        <div
          className="absolute inset-0"
          style={{
            clipPath: 'polygon(100% 50%, 100% 100%, 0 100%, 0 75%)',
            background: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 40%, #43A047 70%, #4CAF50 100%)',
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <img
              src="/red1.png"
              alt="Sustainability artwork"
              className="w-full h-full object-cover object-center"
              style={{
                filter: 'brightness(0) saturate(100%) invert(1)',
              }}
            />
          </div>
        </div>
      </div>

      {/* ───────── Floating Top Navbar ───────── */}
      <header className="relative z-30 pt-4 sm:pt-6 px-4 sm:px-5 lg:px-8 xl:px-12">
        <div className="mx-auto max-w-[1280px]">
          <nav
            className="flex items-center justify-between rounded-full bg-white/95 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_-12px_rgba(0,0,0,0.15)] px-3 sm:px-4 py-0 md:bg-white/95"
            data-testid="floating-navbar"
            style={{
              backgroundColor: 'white'
            }}
          >
            <button
              onClick={() => navigate("/")}
              className="flex items-center h-12 overflow-hidden hover:opacity-90 transition-opacity"
              data-testid="brand-mark"
            >
              <img
                src="/OLDLOGO.png"
                alt={BRAND.short}
                className="h-full py-2 w-auto object-cover rounded-full"
                style={{ aspectRatio: "2/1" }}
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={goDocuments}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-[13px] text-[#2D3D30] hover:text-[#0A1A0F] hover:bg-[#F0F7F0] transition-all font-medium"
                data-testid="nav-documents"
              >
                <BookOpen className="w-4 h-4 opacity-70" />
                Documents
                {!user && <Lock className="w-3.5 h-3.5 opacity-50" />}
              </button>
              <button
                onClick={goBasic}
                className="px-4 py-2 rounded-full text-[13px] text-[#2D3D30] hover:text-[#0A1A0F] hover:bg-[#F0F7F0] transition-all font-medium"
                data-testid="nav-basic"
              >
                Basic Mode
              </button>
              <button
                onClick={goAdvanced}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-[13px] text-[#2D3D30] hover:text-[#0A1A0F] hover:bg-[#F0F7F0] transition-all font-medium"
                data-testid="nav-advanced"
              >
                Advanced Mode
                {!user && <Lock className="w-3.5 h-3.5 opacity-50" />}
              </button>
              <div className="w-px h-5 bg-[#E0E8E0] mx-2" />
              <button
                onClick={goBasic}
                className="inline-flex items-center gap-2 ml-1 pl-4 pr-2 py-2 rounded-full bg-[#0A1A0F] text-white text-[13px] font-medium hover:bg-[#2E7D32] transition-all shadow-lg shadow-[#0A1A0F]/20"
                data-testid="nav-cta"
              >
                Start Assessment
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#43A047]">
                  <ArrowRight className="w-3 h-3 text-white" />
                </span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-full hover:bg-[#F0F7F0] transition-colors relative z-10"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-[#0A1A0F]" />
              ) : (
                <Menu className="w-5 h-5 text-[#0A1A0F]" />
              )}
            </button>
          </nav>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 rounded-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-xl p-4">
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { goDocuments(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-[14px] text-[#2D3D30] hover:bg-[#F0F7F0] transition-all font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  Documents
                  {!user && <Lock className="w-3.5 h-3.5 ml-auto" />}
                </button>
                <button
                  onClick={() => { goBasic(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-[14px] text-[#2D3D30] hover:bg-[#F0F7F0] transition-all font-medium"
                >
                  Basic Mode
                </button>
                <button
                  onClick={() => { goAdvanced(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-[14px] text-[#2D3D30] hover:bg-[#F0F7F0] transition-all font-medium"
                >
                  Advanced Mode
                  {!user && <Lock className="w-3.5 h-3.5 ml-auto" />}
                </button>
                <button
                  onClick={() => { goBasic(); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0A1A0F] text-white text-[14px] font-medium hover:bg-[#2E7D32] transition-all"
                >
                  Start Assessment
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ───────── Hero Section ───────── */}
      <section
        className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-5 lg:px-8 xl:px-12 pt-4 sm:pt-6 lg:pt-8 xl:pt-10 pb-4 sm:pb-6"
        data-testid="hero-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start lg:items-center">
          {/* Left Column - Content */}
          <div className="text-left md:text-left text-center">
            {/* TOSS Badge - centered on mobile */}
            <div className="mb-2 sm:mb-4 flex justify-center md:justify-start">
              <div className="ml-0 md:ml-[-10px] lg:ml-[-10px] inline-flex items-center gap-3 bg-white/60 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-[#E8F0E8] shadow-sm">

                <span className="text-[8px] sm:text-[9px] text-[#3D5A3F] tracking-wide whitespace-nowrap">
                  Product Sustainability Index
                </span>
                <div className="w-px h-4 bg-[#E8F0E8]" /> {/* Divider */}
                <div className="flex items-center gap-1.5">
                  <img
                    src="/Hypes logo.png"
                    alt="Hypes"
                    className="h-4 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                  />
                  <span className="text-[8px] sm:text-[9px] text-[#3D5A3F] font-light tracking-wide">
                    from Hypes.in
                  </span>
                </div>
              </div>
            </div>

            {/* Main Title with Logo Image - centered on mobile */}
            <h1
              className="tracking-[-0.025em] text-[#0A1A0F] leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <div className="flex justify-center md:justify-start ml-0 md:ml-[-40px] lg:ml-[-60px]">
                <img
                  src="/OLDLOGO.png"
                  alt="TOSS Logo"
                  className="h-24 sm:h-32 lg:h-40 w-auto object-contain"
                />
              </div>
              <span className="block font-light text-lg sm:text-xl lg:text-2xl xl:text-3xl text-[#2D3D30] md:text-[#2D3D30]">
                Product
              </span>
              <span className="block font-bold text-lg sm:text-xl lg:text-2xl xl:text-3xl text-[#0A1A0F] md:text-[#0A1A0F] relative">
                Sustainability
                <span className="text-[#7CB342] md:text-[#2E7D32]"> Index</span>
              </span>
            </h1>

            {/* Subtitle with clear context - consistent across all devices */}
            <p
              className="mt-3 sm:mt-4 text-[12px] sm:text-[13px] lg:text-[14px] text-[#3D5A3F] font-light leading-[1.6] max-w-[550px] mx-auto md:mx-0 bg-white/50 backdrop-blur-sm p-3 sm:p-4 rounded-2xl border border-white/40 shadow-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="hero-subline"
            >
              <span className="font-bold text-[#0A1A0F]">TOSS Index</span> is a standardized scoring index that summarizes a product's overall sustainability by transforming complex ESG & product lifecycle data into a single, 0–100 score, helping businesses and consumers compare products using one universal score and make informed decisions.
            </p>

            {/* CTAs - centered on mobile */}
            <div
              className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-center sm:items-start justify-center md:justify-start gap-3 sm:gap-4"
              data-testid="hero-ctas"
            >
              <button
                onClick={goBasic}
                className="group relative inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 pr-1.5 sm:pr-2 py-3 sm:py-3.5 rounded-full bg-[#2A2A2A] text-white shadow-[0_8px_0_#1A1A1A,0_20px_50px_-20px_rgba(0,0,0,0.5)] hover:shadow-[0_4px_0_#1A1A1A,0_25px_60px_-20px_rgba(0,0,0,0.6)] hover:-translate-y-1 active:translate-y-1 active:shadow-[0_2px_0_#1A1A1A] transition-all duration-200 w-full sm:w-auto justify-center transform-gpu"
                data-testid="cta-start-basic"
              >
                <span className="text-[13px] sm:text-[15px] font-semibold tracking-tight">Calculate Your Product Sustainability</span>
                <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#2E7D32] group-hover:bg-[#388E3C] transition-colors shrink-0 shadow-[inset_0_-2px_0_#1A5A1A] group-hover:shadow-[inset_0_-1px_0_#1A5A1A]">
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white group-hover:translate-x-1 group-hover:scale-110 transition-all duration-200" />
                </span>
              </button>

              <button
                onClick={goAdvanced}
                className="group inline-flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 rounded-full text-[13px] sm:text-[15px] text-[#0A1A0F] hover:bg-white/60 hover:backdrop-blur-sm transition-all font-semibold border border-white/20"
                data-testid="cta-try-advanced"
              >
                <span className="opacity-70 group-hover:opacity-100 transition-opacity">Try Advanced Mode</span>
                <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 opacity-50 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                {!user && <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />}
              </button>
            </div>

            {/* Trust Badge - centered on mobile */}
            <div
              className="mt-4 sm:mt-6 inline-flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3 lg:gap-4 px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/30 shadow-[0_2px_18px_-12px_rgba(10,26,15,0.1)]"
              data-testid="hero-info-pill"
            >
              <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11.5px] text-[#2D3D30] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32]" />
                NO LOGIN REQUIRED
              </span>
              <span className="w-px h-3.5 bg-[#E0E8E0]" />
              <span className="text-[10px] sm:text-[11.5px] text-[#3D5A3F]">
                BOM Setup → Insights
              </span>
              <span className="hidden sm:inline-flex w-px h-3.5 bg-[#E0E8E0]" />
              <span className="hidden sm:inline-flex text-[10px] sm:text-[11.5px] text-[#3D5A3F]">
                ESG-aligned
              </span>
            </div>
          </div>

          {/* Right Column - Carousel - hidden on mobile */}
          <div className="hidden lg:flex justify-center lg:justify-end mt-0 lg:mt-0">
            <div className="w-full max-w-[400px] sm:max-w-[460px] md:max-w-[500px] lg:max-w-[540px]">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Combined "HOW IT WORKS" & ESG Pillars Section with Single 3D White Background ───────── */}
      <section className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-5 lg:px-8 xl:px-12 py-2 sm:py-4">
        {/* Single 3D Opaque White Background with heavy shadow and levitating animation */}
        <div className="absolute inset-0 -mx-4 sm:-mx-5 lg:-mx-8 xl:-mx-12 pointer-events-none">
          <div
            className="w-full h-full bg-white rounded-[1.5rem] sm:rounded-[2rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3),0_50px_100px_-20px_rgba(0,0,0,0.2),0_80px_150px_-30px_rgba(0,0,0,0.15),inset_0_-4px_6px_rgba(0,0,0,0.05)] transform-gpu transition-all duration-300 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.35),0_60px_120px_-30px_rgba(0,0,0,0.25),0_100px_180px_-40px_rgba(0,0,0,0.2),inset_0_-4px_6px_rgba(0,0,0,0.05)] hover:-translate-y-2 animate-levitate"
            style={{
              transformStyle: 'preserve-3d',
              perspective: '1000px',
            }}
          />
          {/* 3D Depth Layer */}
          <div
            className="w-full h-full rounded-[1.5rem] sm:rounded-[2rem] absolute inset-0 -z-10 transform translate-y-2 sm:translate-y-3 translate-x-0.5 sm:translate-x-1"
            style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.12) 100%)',
              filter: 'blur(20px)',
            }}
          />
        </div>

        <div className="relative py-6 sm:py-8 px-3 sm:px-4">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#2E7D32] font-semibold flex items-center justify-center gap-2 mb-3">
              <span className="w-6 sm:w-8 h-px bg-[#2E7D32]" /> How It Works <span className="w-6 sm:w-8 h-px bg-[#2E7D32]" />
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl text-[#0A1A0F] tracking-[-0.02em] px-2"
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
            >
              From product BOM to TOSS Index &amp; report
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { num: "01", label: "BOM Setup", desc: "Materials & weights" },
              { num: "02", label: "Stage Inputs", desc: "12 parameters / stage" },
              { num: "03", label: "TOSS Scoring", desc: "Weighted aggregate" },
              { num: "04", label: "Insights", desc: "Actionable report" },
            ].map((step, i) => (
              <div
                key={i}
                className="group relative bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-[#E8F0E8] p-4 sm:p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 hover:bg-white"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: 'translateZ(20px)',
                }}
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#2E7D32] text-white flex items-center justify-center text-xs sm:text-sm font-bold shadow-lg shadow-[#2E7D32]/30">
                  {step.num}
                </div>
                <div className="mt-3 sm:mt-4">
                  <h4 className="text-[14px] sm:text-[15px] font-semibold text-[#0A1A0F]">{step.label}</h4>
                  <p className="text-[12px] sm:text-[13px] text-[#3D5A3F] mt-1">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 text-[#2E7D32]">
                    <ChevronRight className="w-5 h-5" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 sm:mt-8 text-center">
            <button
              onClick={goBasic}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-[#0A1A0F] text-white text-[13px] sm:text-[14px] font-semibold hover:bg-[#2E7D32] transition-all shadow-lg shadow-[#0A1A0F]/20"
            >
              Start Basic Assessment
              <ArrowRight className="w-4 h-4" />
            </button>
            <span className="block sm:inline-block mt-2 sm:mt-0 sm:ml-4 text-[12px] sm:text-[13px] text-[#3D5A3F]">· NO LOGIN REQUIRED</span>
          </div>

          {/* ESG Pillars Section - now inside the same white background */}
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#E8F0E8]">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 max-w-3xl mx-auto">
              {[
                { label: "Environmental", icon: Leaf, color: "#2E7D32" },
                { label: "Social", icon: Users, color: "#388E3C" },
                { label: "Governance", icon: Shield, color: "#1B5E20" },
                { label: "Economic", icon: TrendingUp, color: "#43A047" },
              ].map((pillar) => (
                <div
                  key={pillar.label}
                  className="flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white border border-[#E8F0E8] hover:shadow-md transition-all hover:-translate-y-0.5"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: 'translateZ(10px)',
                  }}
                >
                  <pillar.icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: pillar.color }} />
                  <span className="text-[11px] sm:text-[12px] font-medium text-[#0A1A0F] text-center">{pillar.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-[#F8FBF8] border border-[#E8F0E8]">
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#2E7D32]" />
                <span className="text-[11px] sm:text-[13px] text-[#2D3D30] font-medium">RESEARCH-BACKED METHODOLOGY</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-5 lg:px-8 xl:px-12 pb-4 sm:pb-6">
        <div className="border-t border-white/20 md:border-[#E8F0E8] pt-4 sm:pt-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[10px] sm:text-[10.5px] tracking-[0.12em] text-white md:text-[#FFFFFF]">
            © {new Date().getFullYear()} {BRAND.short} · {BRAND.from}
          </p>
          <p className="text-[10px] sm:text-[10.5px] tracking-[0.08em] text-white md:text-[#ffffff]">
            {BRAND.full}
          </p>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUp { 
          from { opacity: 0; transform: translateY(14px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes float-slow { 
          0%,100% { transform: translateY(0); } 
          50% { transform: translateY(-8px); } 
        }

        @keyframes levitate {
          0%, 100% {
            transform: translateY(0px) rotateX(0deg) rotateY(0deg);
          }
          25% {
            transform: translateY(-8px) rotateX(1deg) rotateY(-1deg);
          }
          75% {
            transform: translateY(-4px) rotateX(-0.5deg) rotateY(1deg);
          }
        }

        .animate-levitate {
          animation: levitate 6s ease-in-out infinite;
        }

        .animate-levitate:hover {
          animation-play-state: paused;
        }

        /* Mobile-specific text colors */
        @media (max-width: 767px) {
          .text-white {
            color: white !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── Helper Components ───

function Users(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function Shield(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

/* ─── HeroCarousel ─── */
const PRODUCT_IMAGES = {
  tshirt: "/Tshirt.png",

};

function HeroCarousel() {
  const slides = [
    { type: "product", img: PRODUCT_IMAGES.tshirt, caption: "T-shirt Sustainability Assessment", duration: 3000 },
    { type: "overview", duration: 3000 },

  ];

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    Object.values(PRODUCT_IMAGES).forEach((src) => {
      const im = new Image();
      im.src = src;
    });
  }, []);

  useEffect(() => {
    // Get the duration for the current slide
    const currentSlide = slides[idx];
    const duration = currentSlide.duration || 3000; // Default to 3 seconds if not specified

    const t = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, duration);

    return () => clearInterval(t);
  }, [idx, slides]); // Re-run when idx changes to reset the timer

  return (
    <div
      className="mx-auto relative z-10 animate-[fadeUp_0.6s_ease-out]"
      data-testid="hero-carousel"
    >
      <div
        className="relative rounded-2xl overflow-hidden bg-transparent"
        style={{ height: "280px", maxHeight: "300px" }}
      >
        {slides.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700 ease-out"
            style={{
              opacity: i === idx ? 1 : 0,
              pointerEvents: i === idx ? "auto" : "none",
            }}
          >
            {s.type === "overview" && <SlideOverview />}
            {s.type === "product" && (
              <SlideProduct img={s.img} caption={s.caption} />
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 sm:mt-4 flex items-center justify-center gap-1.5 sm:gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-5 sm:w-6 bg-[#2E7D32] shadow-sm" : "w-1.5 bg-[#D5E2D8] hover:bg-[#A8DCB4]"
              }`}
          />
        ))}
      </div>
    </div>
  );
}

function SlideOverview() {
  return (
    <div className="w-full h-full flex items-center justify-center p-3 sm:p-5 bg-gradient-to-br from-white via-[#FAFBFA] to-[#F0F7F0]">
      <div className="w-full max-w-[380px] px-1 sm:px-0">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <span className="text-[8px] sm:text-[9px] tracking-[0.25em] uppercase text-[#3D5A3F] font-semibold">
            TOSS · sample
          </span>
          <span className="text-[8px] sm:text-[9px] text-[#2E7D32] font-semibold tracking-wider uppercase">
            Good
          </span>
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <div className="relative shrink-0">
            <svg className="w-[90px] sm:w-[112px] h-[90px] sm:h-[112px] -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" stroke="#EEF3EE" strokeWidth="6" fill="none" />
              <circle
                cx="50"
                cy="50"
                r="44"
                stroke="url(#tossGradMini)"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${(7.4 / 10) * 276.46} 276.46`}
              />
              <defs>
                <linearGradient id="tossGradMini" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#0A1A0F" />
                  <stop offset="100%" stopColor="#2E7D32" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className="text-[24px] sm:text-[30px] leading-none text-[#0A1A0F]"
                style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
              >
                7.4
              </span>
              <span className="text-[7px] sm:text-[8px] tracking-[0.28em] uppercase text-[#3D5A3F] mt-0.5">
                / 10
              </span>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-5 gap-1 sm:gap-1.5">
            {[6.2, 8.1, 7.4, 9.0, 8.5].map((v, i) => (
              <div key={i} className="flex flex-col items-center gap-0.5 sm:gap-1">
                <div className="h-10 sm:h-14 w-full rounded bg-[#F4F8F4] relative overflow-hidden">
                  <div
                    className="absolute bottom-0 left-0 right-0 rounded bg-gradient-to-t from-[#2E7D32] to-[#9CCC9F]"
                    style={{ height: `${v * 10}%` }}
                  />
                </div>
                <span className="text-[6.5px] sm:text-[7.5px] text-[#3D5A3F] tracking-wider uppercase">
                  {["Raw", "Mfg", "Use", "EOL", "Log"][i]}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-[#EEF3EE] flex items-center justify-between">
          <p className="text-[9px] sm:text-[10px] text-[#0A1A0F]">5 stages · 12 parameters</p>
          <span className="inline-flex items-center gap-1 text-[8px] sm:text-[9px] tracking-[0.18em] uppercase text-[#2E7D32] font-semibold">
            <BarChart3 className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Lifecycle weighted
          </span>
        </div>
      </div>
    </div>
  );
}

function SlideProduct({ img, caption }) {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="w-full h-full relative bg-[#FAFBFA]">
      <img
        src={img}
        alt={caption}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setImageLoaded(true)}
      />
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#F4F8F4]">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-[#2E7D32] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
      <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-lg border border-white/50">
          <Leaf className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#2E7D32]" />
        </div>
        <div className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-[#2E7D32]/90 backdrop-blur-sm shadow-lg">
          <Award className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
          <span className="text-[7px] sm:text-[9px] text-white font-semibold tracking-tight">TOSS Tag</span>
        </div>
      </div>
    </div>
  );
}